import React, { useState } from 'react';
import { Field } from 'redux-form';
import TableComponents from 'components/Table';
import TableRow from '@material-ui/core/TableRow';
import Fields from 'components/Fields';
import Store from 'store/store';
import { MetaType } from 'store/modules/Meta/types';
import Forms from 'components/Forms';
import api from 'api';
import Router from 'components/Router/Router';
import { CustomerType } from 'store/modules/Customers/types';
import { Modal } from 'components/Base';
import NewPolicyModal from './NewPolicyModal';
import Icons from 'components/Icons';
import { Notify } from 'utils';
import { FieldCurrency } from 'components/Base';
import { formatCurrency, unFormatCurrency } from 'utils';

const { connect, selectors, actions } = Store;
const { useParams } = Router;

interface ContributionProps {
  contribution: any;
  contributionFrequencies: Array<MetaType>;
  contributionSources: Array<MetaType>;
  policies: any;
  contributionValues: any;
  customers: Array<CustomerType>;
  unallocatedExistingMoney: number;
  getRecommendationsData: any;
  policyTypes: any;
}

const Contribution = ({
  contribution,
  contributionFrequencies,
  contributionSources,
  policies,
  contributionValues,
  customers,
  unallocatedExistingMoney,
  policyTypes,
  getRecommendationsData
}: ContributionProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isNewPolicyModalOpen, setNewPolicyModalOpen] = useState(false);
  const { portfolio } = useParams() as any;
  const allocateToList = Forms.Utils.preparePolicies({ policies, policyTypes });
  const { amount, id, source, frequency, new_money, owner } =
    contributionValues;
  const allocateData = {
    amount: unFormatCurrency(amount),
    source: source,
    frequency: frequency,
    owner: { id: owner }
  };

  const isExistingSourceChoosen = source === '1';
  const isExceedUnallocatedMoney =
    isExistingSourceChoosen &&
    Number(unFormatCurrency(amount)) > Number(unallocatedExistingMoney);

  const isValid =
    !!amount && !!owner && !!source && !!frequency && !isExceedUnallocatedMoney;

  const createNewContribution = () => {
    return api
      .createContribution({
        portfolio,
        newContribution: allocateData
      })
      .then(({ data }: { data: any }) => {
        return data.id;
      })
      .catch(err => {
        Notify.error(err.response.data.message);
        setIsLoading(false);
      });
  };

  const allocateMoney = async ({
    contributionId,
    policyId
  }: {
    contributionId: number;
    policyId: number;
  }) => {
    setIsLoading(true);

    const contribution = contributionId
      ? contributionId
      : await createNewContribution();

    return api
      .allocateMoney({
        portfolio,
        contributionId: contribution,
        policyId: policyId,
        allocateData
      })
      .then(() =>
        getRecommendationsData({ portfolio }).finally(() => {
          setIsLoading(false);
          setNewPolicyModalOpen(false);
        })
      )
      .catch(err => {
        Notify.error(err.response.data.message);
        setIsLoading(false);
      });
  };

  const createNewPolicy = (val: any) => {
    setIsLoading(true);

    const policyData = {
      owner: {
        id: val.owner
      },
      number: val.number,
      type: val.type,
      provider: val.provider
    };

    return api
      .createNewPolicyWithContribution({
        portfolio,
        policy: policyData,
        contribution: allocateData
      })
      .then(() => {
        return getRecommendationsData({ portfolio }).finally(() => {
          setIsLoading(false);
          setNewPolicyModalOpen(false);
        });
      })
      .catch(err => {
        Notify.error(err.response.data.message);
      });
  };

  if (customers.length === 1) contributionValues.owner = customers[0].id;

  return (
    <TableRow>
      <Modal
        isOpen={isNewPolicyModalOpen}
        closeModalFunc={() => setNewPolicyModalOpen(false)}
        disableEscapeKeyDown
        disableBackdropClick
      >
        <NewPolicyModal
          onSubmit={createNewPolicy}
          initialValues={{
            owner: owner,
            closeModalFunc: () => setNewPolicyModalOpen(false)
          }}
        />
      </Modal>
      <TableComponents.TableCell width="250px">
        <Field
          disabled={customers.length === 1}
          name={`${contribution}.owner`}
          fullWidth
          validate={Fields.Validators.required}
          displayEmpty
          options={customers.map((customer: CustomerType) => {
            return {
              name: customer.display_name,
              value: customer.id
            };
          })}
          component={new_money ? Fields.Select : Fields.String}
        />
      </TableComponents.TableCell>
      <TableComponents.TableCell width="120px">
        <FieldCurrency
          name={`${contribution}.amount`}
          fullWidth
          clearfieldinform="recommendations"
          format={(val: any) => {
            if (!val) return '';
            return new_money ? val : formatCurrency(val);
          }}
          validate={
            isExistingSourceChoosen
              ? [
                  Fields.Validators.required,
                  Fields.Validators.isExceededUnnalocatedMoney(
                    unallocatedExistingMoney
                  )
                ]
              : [Fields.Validators.required]
          }
          component={new_money ? Fields.Text : Fields.String}
          placeholder="Amount"
        />
      </TableComponents.TableCell>
      <TableComponents.TableCell>
        <Field
          name={`${contribution}.frequency`}
          fullWidth
          validate={Fields.Validators.required}
          options={contributionFrequencies}
          component={new_money ? Fields.Select : Fields.String}
          placeholder={new_money ? 'Frequency' : ''}
        />
      </TableComponents.TableCell>
      <TableComponents.TableCell>
        <Field
          name={`${contribution}.source`}
          fullWidth
          validate={Fields.Validators.required}
          component={new_money ? Fields.Select : Fields.String}
          options={contributionSources}
          placeholder={new_money ? 'Source' : ''}
        />
      </TableComponents.TableCell>
      <TableComponents.TableCell width="250px">
        {isLoading ? (
          <Icons.LoadingWrapper isLoading={isLoading}>
            <span>Loading...</span>
          </Icons.LoadingWrapper>
        ) : !new_money ? (
          <Field
            name={`${contribution}.destination_policy`}
            fullWidth
            validate={Fields.Validators.required}
            component={Fields.String}
          />
        ) : (
          <Field
            name={`${contribution}.allocate_to`}
            fullWidth
            disabled={!isValid}
            onChange={({ target }: { target: any }) => {
              const { value } = target;

              if (!value) {
                return setNewPolicyModalOpen(true);
              }
              allocateMoney({ contributionId: id, policyId: value });
            }}
            options={allocateToList}
            component={Fields.Select}
          />
        )}
      </TableComponents.TableCell>
    </TableRow>
  );
};

export default connect(
  (state: any) => ({
    contributionSources: selectors.metaSelectors.contributionSources(state),
    contributionFrequencies:
      selectors.metaSelectors.contributionFrequencies(state),
    policyTypes: selectors.metaSelectors.policyTypes(state),
    policies: selectors.caseSelectors.policies(state),
    customers: selectors.customersSelectors.customers(state),
    unallocatedExistingMoney:
      selectors.caseSelectors.unallocatedExistingMoney(state)
  }),
  {
    getRecommendationsData: actions.recommendationActions.getRecommendationData
  }
)(Contribution);
